import { ShoppingCart, Tag, Package, Coffee, Gift, Book, Camera, Lock, Star, Clock, DollarSign, Truck, Download, Calendar } from "lucide-react";
import { getImageWithFallback } from "../helpers/ImageHelpers";
import ShareToSocial from "../components/ShareToSocial";
import { useMyDialog } from "../context/MyDialogProvider";
import MyHeaderLabel from "../components/MyHeaderLabel";

// Types based on your API response
interface ProductAttribute {
  sku?: string;
  brand?: string;
  [key: string]: any;
}

interface PricingItem {
  price_id: string | null;
  price_type: string | null;
  amount: number | null;
  currency_code: string | null;
  unit_type: string | null;
}

interface Inventory {
  inventory_id: string | null;
  inventory_type: string | null;
  quantity: number | null;
  quantity_reserved: number | null;
  quantity_sold: number | null;
  quantity_min: number | null;
  sku: string | null;
  status: string | null;
}

interface Organization {
  organization_id: string;
  provider_id: string;
  organization_name: string;
  organization_code: string;
  organization_type: string;
  city: string;
  country: string;
}

export interface ProductData {
  resource_id: string;
  organization_id: string;
  resource_code: string;
  resource_name: string;
  resource_type: string;
  description: string;
  status: string;
  is_bookable: number;
  is_shippable: number;
  is_tracked: number;
  is_public: number;
  is_featured: number;
  images_json: string[] | string;
  attributes_json: Record<string, ProductAttribute> | string;
  categories_json: string[] | string;
  tags_json: string[] | string;
  slug: string | null;
  weight_value: number | null;
  weight_unit: string | null;
  created_at: number;
  org_organization_id: string;
  org_provider_id: string;
  org_organization_name: string;
  org_organization_code: string;
  org_organization_type: string;
  org_city: string;
  org_country: string;
  pricing_price_id: string | null;
  pricing_price_type: string | null;
  pricing_amount: number | null;
  pricing_currency_code: string | null;
  pricing_unit_type: string | null;
  price_description: string | null;
  inv_inventory_id: string | null;
  inv_inventory_type: string | null;
  inv_quantity: number | null;
  inv_quantity_reserved: number | null;
  inv_quantity_sold: number | null;
  inv_quantity_min: number | null;
  inv_sku: string | null;
  inv_status: string | null;
  org: Organization;
  pricing: PricingItem[];
  inv: Inventory | null;
}

interface ProductCardProps {
  product: ProductData;
  onAddToCart?: () => void;
  onBuyNow?: () => void;
  onClick?: () => void;
}

export default function ProductDetails({
  product,
  onAddToCart,
  onBuyNow,
  onClick,
}: ProductCardProps) {
  const { open } = useMyDialog();

  // Return early if no product data
  if (!product) {
    return (
      <div className="group relative flex flex-col rounded-2xl border border-gray-200 bg-white shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
        <p className="text-gray-500 text-center p-8">No product data available</p>
      </div>
    );
  }

  // Safely parse JSON fields
  const images = safeParseJSON(product.images_json, []);
  const attributes = product.attributes_json || {};
  const attributeType = getAttributeType(product.resource_type, attributes);
  const productAttributes = attributes[attributeType] || {};
  
  const categories = safeParseJSON(product.categories_json, []);
  const tags = safeParseJSON(product.tags_json, []);

  // Get price from either direct fields or pricing array
  const price = getProductPrice(product);
  
  // Calculate stock status
  const inventory = getInventoryInfo(product);
  const availableQuantity = inventory.quantity - inventory.reserved - inventory.sold;
  const isOutOfStock = availableQuantity <= 0;
  const isLowStock = !isOutOfStock && availableQuantity <= (inventory.minThreshold || 5);

  // Get product type icon
  const TypeIcon = getProductTypeIcon(product.resource_type);

  const showDetails = () => {
    open({
      variant: "modal",
      content: <ProductDetails product={product} />,
      header: <MyHeaderLabel label={product.resource_name} />,
    });
  };

  return (
    <div className="group relative flex flex-col rounded-2xl border border-gray-200 bg-white shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
      {/* Image Section */}
      <div 
        onClick={showDetails} 
        className="relative h-48 cursor-pointer overflow-hidden bg-gray-100"
      >
        {getImageWithFallback({
          originalUrl: images[0],
          alt: product.resource_name,
          className: "h-full w-full object-cover group-hover:scale-110 transition-transform duration-500",
        })}

        {/* Product Type Badge */}
        <div className="absolute top-3 left-3 flex items-center gap-1 rounded-full bg-black/70 backdrop-blur-sm px-3 py-1 text-xs font-medium text-white">
          <TypeIcon size={12} />
          <span>{formatResourceType(product.resource_type)}</span>
        </div>

        {/* Stock Status Badge */}
        {isOutOfStock ? (
          <span className="absolute top-3 right-3 rounded-full bg-red-500 px-3 py-1 text-xs font-medium text-white shadow-lg">
            Out of Stock
          </span>
        ) : isLowStock ? (
          <span className="absolute top-3 right-3 rounded-full bg-orange-500 px-3 py-1 text-xs font-medium text-white shadow-lg">
            Low Stock
          </span>
        ) : inventory.quantity > 0 ? (
          <span className="absolute top-3 right-3 rounded-full bg-green-500 px-3 py-1 text-xs font-medium text-white shadow-lg">
            In Stock
          </span>
        ) : null}

        {/* Organization Badge */}
        <div className="absolute bottom-3 left-3 rounded-full bg-white/90 backdrop-blur-sm px-3 py-1 text-xs font-medium text-gray-700 shadow-lg">
          {product.org_organization_name}
        </div>
      </div>

      {/* Content Section */}
      <div className="flex flex-col gap-3 p-4">
        {/* Title & Description */}
        <div>
          <h3 className="text-lg font-bold text-gray-800 line-clamp-2 mb-1">
            {product.resource_name}
          </h3>
          <p className="text-sm text-gray-600 line-clamp-2">
            {product.description}
          </p>
        </div>

        {/* Category & Tags */}
        <div className="flex flex-wrap gap-2">
          {categories.slice(0, 2).map((cat, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
            >
              <Tag size={10} />
              {cat}
            </span>
          ))}
        </div>

        {/* Product-specific attributes */}
        <div className="flex flex-wrap gap-2 text-xs">
          {renderProductAttributes(product.resource_type, productAttributes)}
        </div>

        {/* Price & Action Section */}
        <div className="mt-2 flex items-end justify-between border-t border-gray-100 pt-3">
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-gray-900">
                {price.currency} {price.amount?.toFixed(2)}
              </span>
              {price.unitType && (
                <span className="text-xs text-gray-500">/{price.unitType}</span>
              )}
            </div>
            {inventory.quantity > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {availableQuantity} available
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <button
              disabled={isOutOfStock || product.status !== 'active'}
              onClick={onAddToCart}
              className="inline-flex items-center justify-center gap-1 rounded-xl bg-black px-4 py-2 text-sm text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Add to Cart"
            >
              <ShoppingCart size={16} />
            </button>
            <button
              disabled={isOutOfStock || product.status !== 'active'}
              onClick={onBuyNow}
              className="inline-flex items-center justify-center gap-1 rounded-xl bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Buy Now"
            >
              <DollarSign size={16} />
            </button>
          </div>
        </div>

        {/* Additional Info Row */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-3">
            {product.is_shippable === 1 && (
              <span className="flex items-center gap-1" title="Shippable">
                <Truck size={12} />
                <span>Shipping</span>
              </span>
            )}
            {product.resource_type === 'product_digital' && (
              <span className="flex items-center gap-1" title="Digital Download">
                <Download size={12} />
                <span>Digital</span>
              </span>
            )}
            {product.is_bookable === 1 && (
              <span className="flex items-center gap-1" title="Bookable">
                <Calendar size={12} />
                <span>Bookable</span>
              </span>
            )}
          </div>
          {product.weight_value && (
            <span className="flex items-center gap-1">
              <Package size={12} />
              {product.weight_value} {product.weight_unit}
            </span>
          )}
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {tags.slice(0, 3).map((tag, i) => (
              <span
                key={i}
                className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600"
              >
                #{tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="text-[10px] text-gray-400">+{tags.length - 3}</span>
            )}
          </div>
        )}

        {/* Share Button */}
        <div className="mt-2">
          <ShareToSocial
            url={`${import.meta.env.VITE_FRONTEND_URL || ''}/shop/${product.resource_id}`}
            title={product.resource_name}
          />
        </div>
      </div>
    </div>
  );
}

// Helper Functions
const safeParseJSON = (data: any, defaultValue: any = []) => {
  if (!data) return defaultValue;
  if (Array.isArray(data)) return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return defaultValue;
    }
  }
  return defaultValue;
};

const getAttributeType = (resourceType: string, attributes: any): string => {
  if (!attributes) return 'product';
  
  switch (resourceType) {
    case 'gift_card':
      return 'digital';
    case 'product_digital':
      return 'digital';
    default:
      // Check if attributes has a specific type
      if (attributes.product) return 'product';
      if (attributes.food) return 'food';
      if (attributes.digital) return 'digital';
      return 'product';
  }
};

const getProductPrice = (product: ProductData) => {
  // Try direct pricing fields first
  if (product.pricing_amount !== null && product.pricing_currency_code !== null) {
    return {
      amount: product.pricing_amount,
      currency: product.pricing_currency_code,
      unitType: product.pricing_unit_type
    };
  }
  
  // Try pricing array
  if (product.pricing && product.pricing.length > 0) {
    const price = product.pricing[0];
    return {
      amount: price.amount || 0,
      currency: price.currency_code || '$',
      unitType: price.unit_type
    };
  }
  
  // Default fallback
  return {
    amount: 0,
    currency: '$',
    unitType: null
  };
};

const getInventoryInfo = (product: ProductData) => {
  // Try direct inventory fields first
  if (product.inv_quantity !== null) {
    return {
      quantity: product.inv_quantity || 0,
      reserved: product.inv_quantity_reserved || 0,
      sold: product.inv_quantity_sold || 0,
      minThreshold: product.inv_quantity_min || 5
    };
  }
  
  // Try inv object
  if (product.inv && product.inv.quantity !== null) {
    return {
      quantity: product.inv.quantity || 0,
      reserved: product.inv.quantity_reserved || 0,
      sold: product.inv.quantity_sold || 0,
      minThreshold: product.inv.quantity_min || 5
    };
  }
  
  // Default fallback for digital/non-inventory items
  return {
    quantity: 999,
    reserved: 0,
    sold: 0,
    minThreshold: 5
  };
};

const getProductTypeIcon = (type: string) => {
  switch (type) {
    case 'gift_card':
      return Gift;
    case 'product_digital':
      return Book;
    case 'product_physical':
      return Package;
    default:
      return Tag;
  }
};

const formatResourceType = (type: string): string => {
  return type
    .replace('product_', '')
    .replace('_', ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const renderProductAttributes = (type: string, attributes: any) => {
  const items = [];
  
  switch (type) {
    case 'gift_card':
      if (attributes.denominations) {
        items.push(
          <span key="denom" className="flex items-center gap-1 rounded-full bg-purple-50 px-2 py-1 text-purple-700">
            <DollarSign size={10} />
            Up to ${Math.max(...attributes.denominations)}
          </span>
        );
      }
      if (attributes.expiry_months) {
        items.push(
          <span key="expiry" className="flex items-center gap-1 rounded-full bg-purple-50 px-2 py-1 text-purple-700">
            <Clock size={10} />
            {attributes.expiry_months}mo
          </span>
        );
      }
      break;
      
    case 'product_digital':
      if (attributes.format) {
        items.push(
          <span key="format" className="rounded-full bg-blue-50 px-2 py-1 text-blue-700">
            {attributes.format}
          </span>
        );
      }
      if (attributes.author) {
        items.push(
          <span key="author" className="rounded-full bg-blue-50 px-2 py-1 text-blue-700">
            {attributes.author}
          </span>
        );
      }
      break;
      
    case 'product_physical':
    default:
      if (attributes.brand) {
        items.push(
          <span key="brand" className="rounded-full bg-gray-100 px-2 py-1 text-gray-700">
            {attributes.brand}
          </span>
        );
      }
      if (attributes.material) {
        items.push(
          <span key="material" className="rounded-full bg-gray-100 px-2 py-1 text-gray-700">
            {attributes.material}
          </span>
        );
      }
      if (attributes.color) {
        items.push(
          <span key="color" className="rounded-full bg-gray-100 px-2 py-1 text-gray-700">
            {attributes.color}
          </span>
        );
      }
      if (attributes.size) {
        items.push(
          <span key="size" className="rounded-full bg-gray-100 px-2 py-1 text-gray-700">
            Size {attributes.size}
          </span>
        );
      }
      
      // Food-specific attributes
      if (attributes.cuisine) {
        items.push(
          <span key="cuisine" className="rounded-full bg-orange-50 px-2 py-1 text-orange-700">
            {attributes.cuisine}
          </span>
        );
      }
      if (attributes.dietary?.length) {
        items.push(
          <span key="dietary" className="rounded-full bg-green-50 px-2 py-1 text-green-700">
            {attributes.dietary[0]}
          </span>
        );
      }
      if (attributes.prep_time) {
        items.push(
          <span key="prep" className="flex items-center gap-1 rounded-full bg-orange-50 px-2 py-1 text-orange-700">
            <Clock size={10} />
            {attributes.prep_time}min
          </span>
        );
      }
      break;
  }
  
  return items.slice(0, 3);
};